import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { deviceFieldConfig } from "./deviceConfig";

export default function DeviceDetails({ deviceData, deviceType }) {
    const theme = useTheme();
    const mode = theme.palette.mode;

    const fields = deviceFieldConfig[deviceType?.toLowerCase()] || deviceFieldConfig.default;

    function getStatusDot(value, label) {
        const isRed =
            value === "YES" &&
            (label?.toLowerCase().includes("fault") ||
                label?.toLowerCase().includes("external") ||
                label?.toLowerCase().includes("run") ||
                label?.toLowerCase().includes("vfd"));

        if (isRed) {
            return (
                <Box
                    sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        bgcolor: "error.main",
                        mr: 1,
                        display: "inline-block"
                    }}
                />
            );
        }
        if (
            value === "YES" ||
            value === "Good" ||
            value === "ON" ||
            value === "WET" ||
            value === "EXCELLENT"
        ) {
            return (
                <Box
                    sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        bgcolor: "success.main",
                        mr: 1,
                        display: "inline-block"
                    }}
                />
            );
        }
        if (
            value === "NO" ||
            value === "Bad" ||
            value === "OFF" ||
            value === "DRY"
        ) {
            return (
                <Box
                    sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        bgcolor: mode === "dark" ? "#757575" : "#bdbdbd",
                        mr: 1,
                        display: "inline-block"
                    }}
                />
            );
        }
        return null;
    }
    function renderIcon(icon) {
        if (typeof icon === "function" || (icon?.$$typeof && icon?.render)) {
            const IconComponent = icon;
            return (
                <IconComponent
                    style={{
                        width: 40,
                        height: 40,
                        marginRight: 12,
                        display: "inline-block",
                        fill: theme.palette.primary.main,
                        stroke: theme.palette.custom.text
                    }}
                />
            );
        }
        if (typeof icon === "string") {
            return (
                <img
                    src={icon}
                    alt=""
                    style={{
                        width: 32,
                        height: 32,
                        marginRight: 12,
                        objectFit: "contain"
                    }}
                />
            );
        }
        return null;
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
                width: "100%",
                p: 2
            }}
        >
            {fields.map((field, idx) => {
                let icon = field.icon;
                if (typeof icon === "function") {
                    icon =
                        icon.length === 2
                            ? icon(mode, deviceData)
                            : icon(deviceData);
                }

                const value =
                    typeof field.value === "function"
                        ? field.value(deviceData)
                        : field.value;

                return (
                    <Box
                        key={idx}
                        sx={{
                            flex: "1 1 29%",
                            minWidth: 280,
                            maxWidth: "32%",
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "background.paper",
                            boxShadow: 2,
                            gap: 2,
                            "@media (max-width: 900px)": {
                                maxWidth: "100%",
                                flex: "1 1 100%"
                            }
                        }}
                    >
                        {renderIcon(icon)}
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="subtitle1"
                                fontWeight={700}
                                sx={{ color: "text.primary" }}
                            >
                                {field.label}
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    mt: 0.5
                                }}
                            >
                                {getStatusDot(value, field.label)}
                                <Typography
                                    variant="body1"
                                    fontWeight={600}
                                    sx={{ color: "text.secondary" }}
                                >
                                    {value}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

DeviceDetails.propTypes = {
    deviceData: PropTypes.object,
    deviceType: PropTypes.string
};
